import { isWithinStay, type PlaceEvent, type ScaleEvent, type SpecialEvent } from '../domain/scales'

/** What a timeline entry marks: a berth stay's arrival or departure, or a MARPOL/BOMBEO start or end. */
export type TimelineEntryKind = 'ARRIVAL' | 'DEPARTURE' | 'START' | 'END'

export interface TimelineEntry<E extends ScaleEvent> {
  kind: TimelineEntryKind
  /** ISO-8601: the event's start or end. */
  time: string
  event: E
  /** A MARPOL/BOMBEO's place among the scale's events of its type, by start, from 1; null on a stay. */
  number: number | null
}

export interface TimelineColumn<E extends ScaleEvent> {
  /** The berth stay; null for MARPOL/BOMBEO events that start within no stay. */
  stay: (E & PlaceEvent) | null
  /** Ascending in time. */
  entries: TimelineEntry<E>[]
}

const byStart = (a: ScaleEvent, b: ScaleEvent): number => Date.parse(a.startTime) - Date.parse(b.startTime) || a.id.localeCompare(b.id)

/** At one instant: arrival first, then starts, then ends, departure last. */
const KIND_ORDER: Record<TimelineEntryKind, number> = { ARRIVAL: 0, START: 1, END: 2, DEPARTURE: 3 }

const byTime = <E extends ScaleEvent>(a: TimelineEntry<E>, b: TimelineEntry<E>): number =>
  Date.parse(a.time) - Date.parse(b.time) || KIND_ORDER[a.kind] - KIND_ORDER[b.kind]

/**
 * A scale's events as a timeline: one column per berth stay, by start (a
 * muelle visited twice has two), each with its arrival, departure and the
 * start and end of every MARPOL/BOMBEO that starts during it. When stays
 * overlap, the latest-starting one wins, as for the current muelle. Events that
 * start within no stay share a column without stay, placed by their start.
 */
export const scaleTimeline = <E extends ScaleEvent>(events: readonly E[]): TimelineColumn<E>[] => {
  const stays = events.filter((e): e is E & PlaceEvent => e.type === 'PLACE').sort(byStart)
  const specials = events.filter((e): e is E & SpecialEvent => e.type !== 'PLACE').sort(byStart)

  const numbers = new Map<string, number>()
  const counts: Partial<Record<SpecialEvent['type'], number>> = {}
  for (const event of specials) {
    const number = (counts[event.type] ?? 0) + 1
    counts[event.type] = number
    numbers.set(event.id, number)
  }

  const stayColumns = new Map(stays.map(stay => [stay.id, [] as TimelineEntry<E>[]]))
  for (const stay of stays) {
    const entries = stayColumns.get(stay.id) ?? []
    entries.push({ kind: 'ARRIVAL', time: stay.startTime, event: stay, number: null })
    if (stay.endTime !== null) entries.push({ kind: 'DEPARTURE', time: stay.endTime, event: stay, number: null })
  }

  // Columns in time order: each stay at its start, each stayless event at its own.
  const columns: (TimelineColumn<E> & { at: number })[] = stays.map(stay => ({
    stay,
    entries: stayColumns.get(stay.id) ?? [],
    at: Date.parse(stay.startTime),
  }))
  for (const event of specials) {
    const number = numbers.get(event.id) ?? null
    const marks: TimelineEntry<E>[] = [{ kind: 'START', time: event.startTime, event, number }]
    if (event.endTime !== null) marks.push({ kind: 'END', time: event.endTime, event, number })
    const start = Date.parse(event.startTime)
    const home = [...stays].reverse().find(stay => isWithinStay(stay, start))
    const entries = home ? stayColumns.get(home.id) : undefined
    if (entries) entries.push(...marks)
    else columns.push({ stay: null, entries: marks, at: start })
  }

  // A stay comes before a stayless event starting at the same instant; stayless neighbours merge.
  const ordered = columns.sort((a, b) => a.at - b.at || (a.stay ? 0 : 1) - (b.stay ? 0 : 1))
  const merged: TimelineColumn<E>[] = []
  for (const { stay, entries } of ordered) {
    const previous = merged.at(-1)
    if (stay === null && previous?.stay === null) previous.entries.push(...entries)
    else merged.push({ stay, entries })
  }
  for (const column of merged) column.entries.sort(byTime)
  return merged
}
